<template>
  <v-data-table
    :loading="loading"
    :items="items"
    :headers="getHeaders"
    :dense="dense"
    disable-pagination
    hide-default-footer
  >
    <template v-slot:item.number="{ item }">
      <order-menu
        :order="item"
        :items="item.items"
        :show-icons="showIcons"
        @showOrder="$emit('showOrder', item)"
        @stateEdited="fetchOrders"
      ></order-menu>
    </template>

    <template v-slot:item.orderMode="{ item }">
      <v-btn fab outlined small :color="orderModeColor(item)">
        <span class="text-h6">
          {{ orderMode(item) }}
        </span>
      </v-btn>
    </template>

    <template v-slot:item.contact.name="{ item }">
      <span class="font-weight-regular">{{ item.contact.name }}</span>
    </template>

    <template v-slot:item.location.address="{ item }">
      <span class="font-weight-medium">{{
        shortAddress(item.location.address)
      }}</span>
    </template>

    <template v-slot:item.totalAmount="{ item }">
      <span class="font-weight-bold">{{
        currencyFormat(item.totalAmount)
      }}</span>
    </template>

    <template v-slot:item.payment.confirmed="{ item }">
      <order-payment-confirmed
        :order="item"
        @orderUpdated="fetchOrders"
      ></order-payment-confirmed>
    </template>

    <template v-slot:item.delivery.time="{ item }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-chip :class="getAlarmColor(item.delivery.time, item.delivery.date)" v-on="on">
            {{ getTime(item) }}
          </v-chip>
        </template>
        {{ dayjs(item.delivery.dateTime).format("YYYY-MM-DD HH:mm") }}
      </v-tooltip>
    </template>

    <template v-slot:item.delivery.distance="{ item }">
      {{ item.delivery.distance ? item.delivery.distance.toFixed(1) : 0 }}
    </template>

    <template v-slot:item.addressOrName="{ item }">
      <span
        v-if="item.delivery.mode === 'TAKE_AWAY'"
        class="font-weight-medium"
        >{{ item.contact.name }}</span
      >
      <span v-else class="font-weight-regular">{{
        shortAddress(item.location.address)
      }}</span>
    </template>

    <template v-slot:item.deliveryUser.username="{ item }">
      <delivery-user
        :order="item"
        @orderUpdated="fetchOrders"
        :disabled="deliveryUserReadOnly"
      ></delivery-user>
    </template>

    <template v-slot:item.actions="{ item }">
      <div class="d-flex">
        <order-prev-flow-state
          v-if="actionPrev && state != 'SCHEDULED'"
          :order="item"
          @orderUpdated="onOrderUpdatedPrev"
        ></order-prev-flow-state>
        <order-next-flow-state
          v-if="actionNext && state != 'SCHEDULED'"
          :order="item"
          @orderUpdated="onOrderUpdatedNext"
        />
      </div>
    </template>
  </v-data-table>
</template>

<script>
import OrderMenu from "@/modules/delivery/components-core/business/OrderMenu/OrderMenu.vue";
import { DayjsMixin } from "@dracul/dayjs-frontend";
import OrderPrevFlowState from "../OrderPrevFlowState";
import OrderNextFlowState from "@/modules/delivery/components-core/business/OrderNextFlowState";
import OrderProvider from "@/modules/delivery/providers/OrderProvider";
import DeliveryUser from "@/modules/delivery/components-core/business/DeliveryUser/DeliveryUser.vue";
import OrderPaymentConfirmed from "@/modules/delivery/components-core/business/OrderPaymentConfirmed/OrderPaymentConfirmed.vue";
import CurrencyMixin from "@/modules/delivery/mixins/CurrencyMixin";
import dayjs from "dayjs";
import FlowMixin from "@/modules/delivery/mixins/FlowMixin";

export default {
  name: "OrderFlowTable",
  props: {
    state: { type: String, required: true },
    drinkState: { type: String },
    disableHeaders: { type: Array, default: () => [] },
    dense: { type: Boolean, default: false },
    mode: { type: String },
    showIcons: { type: Boolean, default: true },
    actionPrev: { type: Boolean, default: true },
    actionNext: { type: Boolean, default: true },
    deliveryUserReadOnly: { type: Boolean, default: false },
  },
  components: {
    OrderPaymentConfirmed,
    DeliveryUser,
    OrderPrevFlowState,
    OrderNextFlowState,
    OrderMenu,
  },
  mixins: [CurrencyMixin, DayjsMixin, FlowMixin],
  data() {
    return {
      loading: false,
      items: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("delivery.order.labels.order"), value: "number" },
        { text: this.$t("delivery.order.labels.name"), value: "contact.name" },
        {
          text: this.$t("delivery.order.labels.mode"),
          value: "orderMode",
          align: "center",
          sortable: false,
        },
        { text: this.$t("delivery.cart.total"), value: "totalAmount" },
        { text: this.$t("delivery.payment.paid"), value: "payment.confirmed" },
        { text: this.$t("delivery.mode.distance"), value: "delivery.distance" },
        {
          text: this.$t("delivery.order.labels.address"),
          value: "location.address",
        },
        {
          text: this.$t("delivery.order.labels.addressOrName"),
          value: "addressOrName",
        },
        { text: this.$t("delivery.schedule"), value: "delivery.time" },
        {
          text: this.$t("delivery.order.labels.deliveryUser"),
          value: "deliveryUser.username",
        },
          ...( this.state != 'SCHEDULED' ? [{ text: this.$t("common.actions"), value: "actions" }] : []),
      ];
    },
    getHeaders() {
      return this.headers.filter((h) => !this.disableHeaders.includes(h.value));
    },
  },
  created() {
    this.fetchOrders();
  },
  methods: {
    dayjs,
    isFarAway(order) {
      return this.getMaxKmForMinCost < order.delivery.distance;
    },

    fetchOrders() {
      this.loading = true;
      OrderProvider.fetchOrdersByState(this.state, null, this.mode, this.drinkState)
        .then((r) => {
          this.items = r.data.orderFetchByState;
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onOrderUpdatedPrev(order) {
      this.fetchOrders();
      this.$emit("orderUpdatePrev", order);
    },
    onOrderUpdatedNext(order) {
      this.fetchOrders();
      this.$emit("orderUpdateNext", order);
    },
  },
};
</script>

<style scoped></style>
